import React, { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  startOfYear,
  endOfYear,
  startOfWeek,
  endOfWeek,
  addDays,
  format,
  getYear,
  differenceInCalendarDays,
} from 'date-fns';
import { ko } from 'date-fns/locale';
import './TradingHeatmap.css';

export interface TradingHeatmapInvestment {
  recordDate?: string;
  isDeleted?: boolean;
}

interface TradingHeatmapProps {
  investments: TradingHeatmapInvestment[];
}

/** 일~토 행 중 월·수·금만 표시 (row 1,3,5) */
const WEEKDAY_LEFT_LABELS: (string | null)[] = [null, '월', null, '수', null, '금', null];

function buildCountsByDate(
  investments: TradingHeatmapInvestment[],
  year: number
): { map: Map<string, number>; total: number; max: number } {
  const map = new Map<string, number>();
  const prefix = `${year}-`;
  for (const inv of investments) {
    if (inv.isDeleted) continue;
    const raw = inv.recordDate;
    if (!raw) continue;
    const d = raw.split('T')[0];
    if (!d.startsWith(prefix)) continue;
    map.set(d, (map.get(d) || 0) + 1);
  }
  let total = 0;
  let max = 0;
  map.forEach((n) => {
    total += n;
    if (n > max) max = n;
  });
  return { map, total, max };
}

function levelForCount(count: number, maxDay: number): 0 | 1 | 2 | 3 | 4 {
  if (count === 0) return 0;
  if (maxDay <= 1) return 4;
  const t = count / maxDay;
  if (t <= 0.25) return 1;
  if (t <= 0.5) return 2;
  if (t <= 0.75) return 3;
  return 4;
}

/** 투자 기록이 있는 가장 이른 연도 ~ 올해까지, 최소 5년 폭은 보장 */
function buildSelectableYears(investments: TradingHeatmapInvestment[]): number[] {
  const now = new Date().getFullYear();
  let earliest = now;
  for (const inv of investments) {
    if (inv.isDeleted || !inv.recordDate) continue;
    const y = parseInt(inv.recordDate.split('T')[0].slice(0, 4), 10);
    if (Number.isFinite(y)) earliest = Math.min(earliest, y);
  }
  const start = Math.min(earliest, now - 4);
  const end = now;
  const years: number[] = [];
  for (let y = end; y >= start; y--) years.push(y);
  return years;
}

type TooltipState = { left: number; top: number; lines: string[] };

const TradingHeatmap: React.FC<TradingHeatmapProps> = ({ investments }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectableYears = useMemo(() => buildSelectableYears(investments), [investments]);
  const [selectedYear, setSelectedYear] = useState(() => {
    const y = new Date().getFullYear();
    return y;
  });

  const year = useMemo(() => {
    if (selectableYears.includes(selectedYear)) return selectedYear;
    return selectableYears[0] ?? new Date().getFullYear();
  }, [selectedYear, selectableYears]);

  useEffect(() => {
    if (!selectableYears.includes(selectedYear)) {
      setSelectedYear(selectableYears[0] ?? new Date().getFullYear());
    }
  }, [selectableYears, selectedYear]);

  const { map: countsByDate, total: totalTrades, max: maxDay } = useMemo(
    () => buildCountsByDate(investments, year),
    [investments, year]
  );

  const { gridStart, nWeeks } = useMemo(() => {
    const yStart = startOfYear(new Date(year, 0, 1));
    const yEnd = endOfYear(new Date(year, 0, 1));
    const start = startOfWeek(yStart, { weekStartsOn: 0 });
    const end = endOfWeek(yEnd, { weekStartsOn: 0 });
    const days = differenceInCalendarDays(end, start) + 1;
    const weeks = Math.ceil(days / 7);
    return { gridStart: start, nWeeks: weeks };
  }, [year]);

  // 모바일 등 가로 스크롤이 있을 때: 가운데 정렬로 인해 초기 뷰가 연중간(월)으로 잡히는 문제 방지 → 항상 연초(1월)부터
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = 0;
  }, [year, nWeeks]);

  const monthLabels = useMemo(() => {
    const labels: (string | null)[] = Array(nWeeks).fill(null);
    for (let m = 0; m < 12; m++) {
      const first = new Date(year, m, 1);
      const weekStart = startOfWeek(first, { weekStartsOn: 0 });
      const col = Math.floor(differenceInCalendarDays(weekStart, gridStart) / 7);
      if (col >= 0 && col < nWeeks && labels[col] == null) {
        labels[col] = format(first, 'MMM', { locale: ko });
      }
    }
    return labels;
  }, [year, nWeeks, gridStart]);

  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  const showTooltip = useCallback((el: HTMLElement, labelLines: string[]) => {
    if (!labelLines.length) return;
    const rect = el.getBoundingClientRect();
    setTooltip({
      left: rect.left + rect.width / 2,
      top: rect.top,
      lines: labelLines,
    });
  }, []);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  const renderCell = (row: number, col: number) => {
    const day = addDays(gridStart, col * 7 + row);
    const inYear = getYear(day) === year;
    const key = format(day, 'yyyy-MM-dd');
    const count = inYear ? countsByDate.get(key) || 0 : 0;
    const level = inYear ? levelForCount(count, maxDay) : 0;
    const dateLabel = format(day, 'yyyy년 M월 d일', { locale: ko });
    const lines = inYear
      ? [dateLabel, count === 0 ? '거래 없음' : `거래 ${count}건`]
      : [];

    return (
      <div
        key={`${row}-${col}`}
        className={`trading-heatmap__cell trading-heatmap__cell--lvl-${inYear ? level : 'out'}`}
        role="img"
        aria-label={inYear ? `${dateLabel}, 거래 ${count}건` : undefined}
        onMouseEnter={(e) => inYear && showTooltip(e.currentTarget, lines)}
        onMouseLeave={hideTooltip}
        onFocus={(e) => inYear && showTooltip(e.currentTarget, lines)}
        onBlur={hideTooltip}
        tabIndex={inYear ? 0 : -1}
      />
    );
  };

  return (
    <div className="trading-heatmap" aria-label={`${year}년 일별 거래 횟수`}>
      <div className="trading-heatmap__top">
        <span className="trading-heatmap__summary">
          <strong>{year}</strong>년 누적 <strong>{totalTrades}</strong>건 거래
        </span>
      </div>

      <div className="trading-heatmap__scroll" ref={scrollRef}>
        <div className="trading-heatmap__inner">
          <div
            className="trading-heatmap__month-row"
            aria-hidden="true"
            style={{
              gridTemplateColumns: `var(--heatmap-gutter, 30px) repeat(${nWeeks}, var(--heatmap-cell, 14px))`,
            }}
          >
            <div className="trading-heatmap__corner" />
            {monthLabels.map((label, i) => (
              <div key={i} className="trading-heatmap__month-label">
                {label ?? ''}
              </div>
            ))}
          </div>

          <div className="trading-heatmap__grid-wrap">
            <div className="trading-heatmap__weekday-col" aria-hidden="true">
              {WEEKDAY_LEFT_LABELS.map((lb, i) => (
                <div key={i} className="trading-heatmap__weekday-label">
                  {lb ?? ''}
                </div>
              ))}
            </div>
            <div className="trading-heatmap__weeks">
              {Array.from({ length: 7 }, (_, row) => (
                <div
                  key={row}
                  className="trading-heatmap__week-row"
                  style={{
                    gridTemplateColumns: `repeat(${nWeeks}, var(--heatmap-cell, 14px))`,
                  }}
                >
                  {Array.from({ length: nWeeks }, (_, col) => renderCell(row, col))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="trading-heatmap__legend">
        <span className="trading-heatmap__legend-text">적음</span>
        <div className="trading-heatmap__legend-cells">
          <div className="trading-heatmap__cell trading-heatmap__cell--lvl-0 trading-heatmap__cell--legend" />
          <div className="trading-heatmap__cell trading-heatmap__cell--lvl-1 trading-heatmap__cell--legend" />
          <div className="trading-heatmap__cell trading-heatmap__cell--lvl-2 trading-heatmap__cell--legend" />
          <div className="trading-heatmap__cell trading-heatmap__cell--lvl-3 trading-heatmap__cell--legend" />
          <div className="trading-heatmap__cell trading-heatmap__cell--lvl-4 trading-heatmap__cell--legend" />
        </div>
        <span className="trading-heatmap__legend-text">많음</span>
      </div>

      <aside className="trading-heatmap__year-panel" aria-label="연도 선택">
        <span className="trading-heatmap__year-panel-title">연도</span>
        <div className="trading-heatmap__year-list" role="listbox">
          {selectableYears.map((y) => (
            <button
              key={y}
              type="button"
              role="option"
              aria-selected={y === year}
              className={`trading-heatmap__year-btn ${y === year ? 'is-active' : ''}`}
              onClick={() => setSelectedYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      </aside>

      {tooltip &&
        createPortal(
          <div
            className="trading-heatmap__tooltip"
            style={{ left: tooltip.left, top: tooltip.top }}
            role="tooltip"
          >
            {tooltip.lines.map((line, i) => (
              <div key={i} className={i === 0 ? 'trading-heatmap__tooltip-date' : 'trading-heatmap__tooltip-meta'}>
                {line}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default TradingHeatmap;
